import { useState, type ReactNode } from 'react'
import type { Frame } from '../../shared/types'
import { navigate } from '../App'
import { readingOrder } from '../../shared/frameOrder'
import { MAX_PDF_FRAMES } from '../../shared/pdfLimits'
import { downloadFrameExports, downloadFramesPdf } from '../lib/frameExport'
import { posthog } from '../lib/posthog'
import { sendFramesToCanva, useCanvaStatus } from '../integrations/canva/export'
import { FigmaLogo } from '../integrations/figma/Tile'
import { CanvaMark, ImportStepFooter, ImportStepHeader, PhotoshopMark, SourcePicker } from './ImportSources'
import { Button } from './ui/button'
import { FrameThumb } from './FramePreview'
import { Checkbox } from './ui/checkbox'
import { PdfIcon } from './ui/icons'
import { Modal, ModalActions, ModalLede, ModalTitle } from './ui/modal'
import { SelectionBar, SelectionBarAction } from './ui/selection-bar'
import { useT, type Translate } from '@/lib/i18n'

export type ExportDestination = 'pdf' | 'psd' | 'canva' | 'figma'

/** Destinations that ask which frames next; Figma takes the selection as is. */
type ChecklistDestination = Exclude<ExportDestination, 'figma'>

interface ChecklistCopy {
  icon: ReactNode
  title: string
  lede: string
  note: string
  action: string
}

/** The words around the frame checklist for each destination that has one. */
function checklistCopy(t: Translate, destination: ChecklistDestination, count: number): ChecklistCopy {
  switch (destination) {
    case 'pdf':
      return {
        icon: <PdfIcon size={22} />,
        title: t('panels.exportPdfTitle'),
        lede: t('panels.exportPdfLede'),
        note: t('panels.exportPdfHint'),
        action: count > 1 ? t('panels.exportDownloadPdfPages', { count }) : t('panels.exportDownloadPdf'),
      }
    case 'psd':
      return {
        icon: <PhotoshopMark size={24} />,
        title: t('hosted.exportPsdTitle'),
        lede: t('hosted.exportPsdLede'),
        note: t('hosted.exportPsdNote'),
        action: count > 1 ? t('hosted.exportPsdActionMany', { count }) : t('hosted.exportPsdAction'),
      }
    case 'canva':
      return {
        icon: <CanvaMark size={24} />,
        title: t('hosted.exportCanvaTitle'),
        lede: t('hosted.exportCanvaLede'),
        note: t('hosted.exportCanvaNote'),
        action: t('hosted.exportCanvaAction', { count }),
      }
  }
}

/**
 * The top bar's Export, in two steps: a picker of destinations mirroring the
 * import modal's source cards, then the page's frames as a checklist (the
 * canvas selection pre-checked, or every frame when nothing is selected).
 * PSD and Canva run from the second step and close the modal — their
 * outcome lands in the canvas toast. PDF binds the checked frames into one
 * document, a page per frame in reading order, which the checklist shows. Figma skips the checklist: it hands the
 * selection, or the whole page, to its copy-for-the-plugin dialog.
 */
export function ExportModal({
  frames,
  initialSelection,
  onClose,
  onSendToFigma,
}: {
  /** every frame on the current page, in layer order */
  frames: Frame[]
  /** frame ids checked when the modal opens; empty checks them all */
  initialSelection: string[]
  onClose: () => void
  onSendToFigma: (frames: Frame[]) => void
}) {
  const canva = useCanvaStatus()
  const [destination, setDestination] = useState<ChecklistDestination | null>(null)
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initialSelection.length ? initialSelection : frames.map((frame) => frame.id)),
  )
  /* a PDF's pages follow reading order, so the list numbers its pages and
     the PDF takes the checked frames in exactly that order (re-sorting only
     the checked ones could regroup the rows) */
  const listed = destination === 'pdf' ? readingOrder(frames) : frames
  const chosen = listed.filter((frame) => selected.has(frame.id))
  /* the server refuses a longer PDF: keep the checklist open until it fits */
  const overPdfLimit = destination === 'pdf' && chosen.length > MAX_PDF_FRAMES
  /* Figma takes the selection as it was when Export opened — never a
     checklist cleared on another destination's step */
  const figmaFrames = initialSelection.length ? frames.filter((frame) => initialSelection.includes(frame.id)) : frames
  const t = useT()

  function toggle(frameId: string) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(frameId)) next.delete(frameId)
      else next.add(frameId)
      return next
    })
  }

  function pick(id: string) {
    const picked = id as ExportDestination
    posthog.capture('frames_export_picked', { destination: picked, frame_count: frames.length })
    if (picked === 'figma') onSendToFigma(figmaFrames)
    else setDestination(picked)
  }

  function run() {
    if (!destination || !chosen.length || overPdfLimit) return
    posthog.capture('frames_exported', { destination, frame_count: chosen.length })
    if (destination === 'pdf') void downloadFramesPdf(chosen[0]!.canvasId, chosen)
    else if (destination === 'psd') void downloadFrameExports(chosen, 'psd')
    else void sendFramesToCanva(chosen)
    onClose()
  }

  function openIntegrations() {
    onClose()
    navigate('/integrations')
  }

  if (!frames.length) {
    return (
      <Modal size="sm" onClose={onClose}>
        <ModalTitle>{t('panels.exportNothing')}</ModalTitle>
        <ModalLede>{t('panels.exportNothingLede')}</ModalLede>
        <ModalActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        </ModalActions>
      </Modal>
    )
  }

  let body: ReactNode
  if (destination === null) {
    body = (
      <SourcePicker
        title={t('hosted.exportTitle')}
        lede={t('hosted.exportLede')}
        onPick={pick}
        sources={[
          {
            id: 'pdf',
            title: t('hosted.exportPdfSource'),
            blurb: t('hosted.exportPdfSourceBlurb'),
            icon: <PdfIcon size={22} />,
          },
          {
            id: 'psd',
            title: t('hosted.psdSource'),
            blurb: t('hosted.exportPsdSourceBlurb'),
            icon: <PhotoshopMark size={24} />,
          },
          /* a self-host without Canva credentials has no Canva to offer */
          ...(canva?.enabled === false
            ? []
            : [
                {
                  id: 'canva',
                  title: t('hosted.canvaSource'),
                  blurb:
                    canva?.connected === false
                      ? t('hosted.exportCanvaSourceConnect')
                      : t('hosted.exportCanvaSourceBlurb'),
                  icon: <CanvaMark size={24} />,
                },
              ]),
          {
            id: 'figma',
            title: t('hosted.exportFigmaSource'),
            blurb: t('hosted.exportFigmaSourceBlurb'),
            icon: <FigmaLogo width={15} height={22} />,
          },
        ]}
      />
    )
  } else if (destination === 'canva' && !canva?.connected) {
    body = (
      <>
        <ImportStepHeader icon={<CanvaMark size={24} />} title={t('hosted.exportCanvaTitle')} />
        <ModalLede>{t('hosted.exportCanvaConnectLede')}</ModalLede>
        <ImportStepFooter note={t('hosted.exportCanvaConnectNote')} onBack={() => setDestination(null)}>
          <Button variant="primary" onClick={openIntegrations}>
            {t('hosted.openIntegrations')}
          </Button>
        </ImportStepFooter>
      </>
    )
  } else {
    const copy = checklistCopy(t, destination, chosen.length)
    body = (
      <>
        <ImportStepHeader icon={copy.icon} title={copy.title} />
        <ModalLede>{copy.lede}</ModalLede>
        <SelectionBar
          className="mt-5"
          count={chosen.length}
          total={frames.length}
          actions={
            <>
              <SelectionBarAction onClick={() => setSelected(new Set(frames.map((frame) => frame.id)))}>
                {t('panels.exportSelectAll')}
              </SelectionBarAction>
              <SelectionBarAction onClick={() => setSelected(new Set())}>{t('panels.exportClear')}</SelectionBarAction>
            </>
          }
        />
        <div
          className="max-h-[calc(100dvh-390px)] overflow-y-auto rounded-[11px] border border-line bg-paper sm:max-h-[min(350px,calc(100vh-390px))]"
          role="group"
          aria-label={t('panels.exportFramesAria')}
        >
          {listed.map((frame, index) => (
            <label
              key={frame.id}
              className="relative grid min-h-[58px] cursor-pointer grid-cols-[20px_24px_64px_minmax(0,1fr)] items-center gap-2.5 border-b border-line bg-surface px-3 py-[9px] first:rounded-t-[10px] last:rounded-t-none last:rounded-b-[10px] last:border-b-0 hover:bg-paper"
            >
              <Checkbox checked={selected.has(frame.id)} onChange={() => toggle(frame.id)} />
              <span className="font-mono text-[9.5px] text-ink-faint">{String(index + 1).padStart(2, '0')}</span>
              <FrameThumb frameId={frame.id} />
              <span className="min-w-0">
                <b className="block overflow-hidden whitespace-nowrap text-ellipsis text-[12.5px] text-ink">
                  {frame.name || t('panels.exportUntitled')}
                </b>
                <span className="mt-[3px] block font-mono text-[10px] text-ink-faint">
                  {Math.round(frame.width)} × {Math.round(frame.height)}
                </span>
              </span>
            </label>
          ))}
        </div>
        <ImportStepFooter
          note={
            overPdfLimit
              ? `${t('panels.exportPdfMax', { count: MAX_PDF_FRAMES })} ${t('panels.exportUncheck', { count: chosen.length - MAX_PDF_FRAMES })}`
              : copy.note
          }
          onBack={() => setDestination(null)}
        >
          <Button variant="primary" disabled={!chosen.length || overPdfLimit} onClick={run}>
            {copy.action}
          </Button>
        </ImportStepFooter>
      </>
    )
  }

  return (
    <Modal size="xl" className="sm:max-w-[820px]" onClose={onClose}>
      {body}
    </Modal>
  )
}
